import { makeObservable, computed } from 'mobx';
import { NodeBaseDto } from '../../types';
import { NodeBaseModel, InputBaseModel, WorksheetModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';
import { InputFactory, InputIds, InstanceUtils } from '../../misc';
import { TypeUtils } from '../../../common/misc';
import { Utils } from '../../../custom_shared/misc';

export default class EntryCodeNodeModel extends NodeBaseModel {
    inputs: InputBaseModel[] = [];

    constructor(data: NodeBaseDto, sources: SourceModelBase[], worksheet: WorksheetModel, parentNode?: NodeBaseModel) {
        super(data, worksheet, parentNode);

        this.inputs = InputFactory.createInputs(this, data.inputs, sources);

        makeObservable(this, {
            sectionInput: computed,
            accountingClassInput: computed,
            headlineLossInput: computed,
            entryCodeValueInput: computed,
            entryCodeShareInput: computed,
            entryCodeSumInput: computed,
            differenceMessage: computed,
            position: computed
        });
    }

    update(data: NodeBaseDto, sources: SourceModelBase[]) {
        this.inputs = InputFactory.createOrUpdateInputs(this, this.inputs, data.inputs, sources);
    }

    get position(): number {
        return (this.parentNode ? this.parentNode.childNodes.indexOf(this) : 0) + 1;
    }

    get name() {
        return undefined;
    }

    get sectionInput() {
        return this.getInputById(InputIds.EntryCodeNode.Section);
    }

    get accountingClassInput() {
        return this.getInputById(InputIds.EntryCodeNode.AccountingClass);
    }

    get headlineLossInput() {
        return this.getInputById(InputIds.EntryCodeNode.HeadlineLoss);
    }

    get entryCodeValueInput() {
        return this.getInputById(InputIds.EntryCodeNode.EntryCodeValue);
    }

    get entryCodeShareInput() {
        return this.getInputById(InputIds.EntryCodeNode.EntryCodeShare);
    }

    get entryCodeSumInput() {
        return this.getInputById(InputIds.EntryCodeNode.EntryCodeSum);
    }

    get signedShareValue() {
        return InstanceUtils.isBalanceInformationNode(this.parentNode) ? this.parentNode.signedShareValue : null;
    }

    get differenceMessage() {
        const signedShareValue = this.signedShareValue;
        const entryCodeShare = this.entryCodeShareInput?.value;
        const entryCodeSum = this.entryCodeSumInput?.value;

        if (
            !TypeUtils.isNumber(signedShareValue) ||
            !TypeUtils.isNumber(entryCodeShare) ||
            !TypeUtils.isNumber(entryCodeSum)
        ) {
            return null;
        }

        // Calculate expected entry code share based on signed share and entry code sum
        const expected = (entryCodeSum * signedShareValue) / 100;
        const difference = expected - entryCodeShare;
        const absDifference = Math.abs(difference);

        // Check tolerance ≤ 0.1% of entryCodeSum
        const percentageTolerance = (entryCodeSum * 0.1) / 100;
        const absPercentageTolerance = Math.abs(percentageTolerance);

        if (absDifference === 0 || absDifference <= absPercentageTolerance) {
            return null; // Within tolerance, treat as OK
        }

        return {
            expectedLabel: `Expected ${Utils.formatAmountWithTwoDecimals(expected)} at ${signedShareValue}% from 100% = ${Utils.formatAmountWithTwoDecimals(entryCodeSum)}`,
            differenceLabel: `Difference = ${Utils.formatAmountWithTwoDecimals(difference)}`
        };
    }

    get dto(): NodeBaseDto {
        return {
            id: this.id,
            type: this.type,
            inputs: this.inputs.map(input => input.dto),
            childNodes: this.childNodes.map(node => node.dto)
        };
    }
}
